// frontend/src/components/AvisoDisplay.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';
import { Bell, X, ChevronLeft, ChevronRight } from 'lucide-react';

// --- Componente de Áudio MODIFICADO ---
// (Substituído BeepPlayer por AudioPlayer)
const AudioPlayer = ({ play, onFinished }) => {
    const audioRef = useRef(null);

    // Efeito para criar o objeto de áudio e adicionar o listener 'ended'
    useEffect(() => {
        if (!audioRef.current) {
            // Caminho para o seu arquivo de áudio na pasta /public
            // !!! IMPORTANTE: Você deve adicionar o arquivo 'notificacao.mp3' 
            // na pasta 'frontend/public/' para este som funcionar !!!
            audioRef.current = new Audio('/notificacao.mp3'); 
            
            // Adiciona o listener 'ended'
            audioRef.current.addEventListener('ended', onFinished);
        }
        
        const audio = audioRef.current; // Cópia para o cleanup

        // Cleanup
        return () => {
            if (audio) {
                audio.removeEventListener('ended', onFinished);
            }
        };
    }, [onFinished]); // Depende apenas do onFinished

    // Efeito para TENTAR tocar o som quando a prop 'play' mudar para true
    useEffect(() => {
        if (play && audioRef.current) {
            // Garante que o som toque do início
            audioRef.current.currentTime = 0;
            
            audioRef.current.play().catch(e => {
                // MODIFICADO: Apenas loga se NÃO for NotAllowedError
                if (e.name !== 'NotAllowedError') {
                    console.error("Erro ao tocar áudio:", e);
                    onFinished(); // Reseta se for um erro real (ex: ficheiro não encontrado)
                }
                // Se for NotAllowedError, NÃO chama onFinished,
                // mantendo 'play' como true para a próxima tentativa.
            });
        }
    }, [play]); // Depende apenas do 'play'

    // NOVO: Efeito para TENTAR NOVAMENTE tocar o som em re-renderizações (após interação)
    // Isso resolve o NotAllowedError assim que o usuário interagir
    useEffect(() => {
        if (play && audioRef.current && audioRef.current.paused) {
            // Tenta tocar novamente (ex: após um clique de menu que causou re-render)
            audioRef.current.play().catch(e => {
                // Continua a suprimir o NotAllowedError
                if (e.name !== 'NotAllowedError') {
                    console.error("Erro ao tentar tocar áudio novamente:", e);
                    onFinished();
                }
            });
        }
    // Este efeito corre em cada render se 'play' for true
    // A função 'onFinished' (estabilizada por useCallback no pai) garante que isto não cause loops
    }, [play, onFinished]);


    return null; // O componente não renderiza nada visualmente
};


// --- Componente de Notificação (Toast) ---
// (Sem alterações)
const Toast = ({ toast, onClose, onNext, onPrev, currentIndex, totalToasts }) => {
    return (
        <div 
            className="w-full max-w-sm p-4 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
            role="alert"
        >
            <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                        <Bell className="w-6 h-6 text-blue-600" />
                    </div>
                </div>
                {/* Layout corrigido (removido w-0) */}
                <div className="flex-1 ml-3">
                    <p className="font-semibold text-gray-900">{toast.title}</p>
                    {toast.message && <p className="mt-1 text-sm text-gray-600">{toast.message}</p>}
                </div>
                <div className="flex-shrink-0 ml-4">
                    {/* Botão "X" agora só chama onClose */}
                    <button 
                        onClick={onClose}
                        className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <span className="sr-only">Fechar</span>
                        <X size={18} />
                    </button>
                </div>
            </div>
            
            {/* NOVO: Navegação (só aparece se houver mais de 1) */}
            {totalToasts > 1 && (
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200">
                    <button 
                        onClick={onPrev}
                        className="p-1 rounded-full text-gray-500 hover:bg-gray-100"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <span className="text-xs font-medium text-gray-600">
                        Aviso {currentIndex + 1} de {totalToasts}
                    </span>
                    <button 
                        onClick={onNext}
                        className="p-1 rounded-full text-gray-500 hover:bg-gray-100"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

// --- Componente Principal ---
// MODIFICADO: Recebe refreshTrigger para atualização forçada
const AvisoDisplay = ({ onUnreadCountChange, avisoSettings = { playSound: true, showPopup: true }, refreshTrigger = 0 }) => {
    const { apiFetch } = useApi();
    const [toasts, setToasts] = useState([]); // Armazena os avisos buscados
    // const [playSound, setPlaySound] = useState(false); // REMOVIDO
    const [shouldPlayBeep, setShouldPlayBeep] = useState(false); // (Nome mantido, mas agora toca áudio)
    const [isDisplayVisible, setIsDisplayVisible] = useState(false);
    const [currentToastIndex, setCurrentToastIndex] = useState(0);
    const knownToastIds = useRef(new Set());
    // REMOVIDO: Estado 'hasInteracted' e seu useEffect
    
    // --- LÓGICA MODIFICADA (DIVIDIDA EM DOIS EFEITOS) ---

    // 1. Efeito de Polling (Busca de Dados e Contagem)
    // Este efeito busca os dados e ATUALIZA O CONTADOR (Sidebar) incondicionalmente.
    // Depende de 'avisoSettings.aviso_check_interval' e 'refreshTrigger'.
    useEffect(() => {
        const fetchAvisosAndCount = async () => {
            try {
                const data = await apiFetch('/avisos/my'); // /my só retorna não lidos
                
                // 1A. ATUALIZA O CONTADOR (SEMPRE)
                // Isto é o que atualiza o número no Sidebar.
                onUnreadCountChange(data.length);

                // 1B. ATUALIZA O ESTADO LOCAL (toasts)
                // Isso vai disparar o segundo useEffect (o de exibição)
                setToasts(data);

            } catch (error) {
                console.error('Erro ao buscar avisos:', error.message);
                onUnreadCountChange(0); // Zera a contagem em caso de erro
            }
        };

        // Define o intervalo (padrão 60s se não configurado ou inválido)
        // O valor vem em segundos do backend, convertemos para ms
        const intervalSeconds = parseInt(avisoSettings.aviso_check_interval) || 60;
        const intervalMs = intervalSeconds * 1000;

        fetchAvisosAndCount(); // Busca inicial
        const intervalId = setInterval(fetchAvisosAndCount, intervalMs); // Polling dinâmico
        
        return () => clearInterval(intervalId);
    // Este useEffect depende do apiFetch, callback e AGORA do intervalo configurado E do gatilho
    }, [apiFetch, onUnreadCountChange, avisoSettings.aviso_check_interval, refreshTrigger]);


    // 2. Efeito de Exibição (Pop-up e Som)
    // Este efeito "reage" à mudança no estado 'toasts' (do Efeito 1)
    // e às 'avisoSettings' (das props).
    useEffect(() => {
        // Se não houver toasts, esconde tudo e limpa o 'knownIds'
        if (toasts.length === 0) {
            setIsDisplayVisible(false);
            knownToastIds.current = new Set();
            return;
        }

        // Lógica para tocar som (apenas se houver NOVOS avisos)
        let foundNew = false;
        const newIds = new Set();
        
        toasts.forEach(toast => {
            newIds.add(toast.id);
            if (!knownToastIds.current.has(toast.id)) {
                foundNew = true; // Encontrou um aviso que não estava na lista anterior
            }
        });

        // Atualiza o 'knownIds' para o próximo ciclo
        knownToastIds.current = newIds;

        // Resetar o índice para o primeiro aviso
        setCurrentToastIndex(0); 

        // 2A. DECIDE SE MOSTRA O POP-UP (CONDICIONAL)
        if (avisoSettings.showPopup) {
            setIsDisplayVisible(true); // Mostra o pop-up
        } else {
            setIsDisplayVisible(false); // Garante que esteja escondido
        }

        // 2B. DECIDE SE TOCA O SOM (CONDICIONAL)
        // MODIFICADO: Removemos o 'hasInteracted' daqui. O AudioPlayer vai tratar disso.
        if (foundNew && avisoSettings.playSound) {
            setShouldPlayBeep(true); // (Irá disparar o AudioPlayer)
        }
    // Este efeito reage aos dados e às configurações
    }, [toasts, avisoSettings]);
    
    
    // 3. Funções de Navegação e Fechamento (Inalteradas)
    const handleClose = () => {
        setIsDisplayVisible(false); // Apenas esconde, não marca como lido
    };
    
    const handleNext = () => {
        setCurrentToastIndex(prev => (prev + 1) % toasts.length);
    };
    
    const handlePrev = () => {
        setCurrentToastIndex(prev => (prev - 1 + toasts.length) % toasts.length);
    };

    // 4. Callback memoizada para o 'onFinished' do áudio
    // CORREÇÃO: Usando useCallback para estabilizar a função (evita tocar em cada render)
    const handleAudioFinished = useCallback(() => {
        setShouldPlayBeep(false);
    }, []); // Dependência vazia garante que a função é estável

    // O pop-up só é visível se houver avisos E o usuário não o tiver fechado (isDisplayVisible)
    const currentToast = toasts[currentToastIndex];

    return (
        <>
            <div className="fixed bottom-4 right-4 z-50 space-y-3">
                {/* 'isDisplayVisible' agora é controlado pelo Efeito 2 */}
                {isDisplayVisible && currentToast && (
                    <Toast 
                        key={currentToast.id} 
                        toast={currentToast} 
                        onClose={handleClose}
                        onNext={handleNext}
                        onPrev={handlePrev}
                        currentIndex={currentToastIndex}
                        totalToasts={toasts.length}
                    />
                )}
            </div>
            {/* MODIFICADO: Chamando AudioPlayer com a callback memoizada */}
            <AudioPlayer 
                play={shouldPlayBeep} 
                onFinished={handleAudioFinished} 
            />
        </>
    );
};

export default AvisoDisplay;